import { RTDBPath } from 'balus-common';
import type { GroupId } from 'balus-common';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { useCurrentUserId } from '@framework/auth';

/**
 * AIアシスタントが有効かどうかを共通化したフック。
 * グループのAIアシスタント有効フラグ（管理画面で設定）と、
 * メンバー単位の利用許可フラグ（グループコンソールで設定）の両方がONのときのみ有効。
 * 初期値が確定するまでは loading=true。ロード完了時に enabled が確定する。
 * 読み取り権限がない等でRTDBの取得に失敗した場合は enabled=false / loading=false に解決する。
 */
export function useIsAIAssistantEnabled(groupId: GroupId): { enabled: boolean; loading: boolean } {
  const currentUserId = useCurrentUserId();

  const [groupEnabled, { loading: groupLoading, error: groupError }] = useSnapshot({
    path: RTDBPath.Group.aiAssistantEnabledPath(groupId),
    load: ({ snapshot }) => !!snapshot.val(),
  });

  // 未ログイン時は userId が無くメンバーパスを組めないため購読自体をスキップする。
  // skip=true の間 path は参照されないが、useSnapshot が有効なパスを要求するためグループフラグのパスをダミーとして渡す。
  const [memberEnabled, { loading: memberLoading, error: memberError }] = useSnapshot({
    path: currentUserId
      ? RTDBPath.Group.aiAssistantMemberEnabledPath(groupId, currentUserId)
      : RTDBPath.Group.aiAssistantEnabledPath(groupId),
    // NoAssign（'N/A'）でtruthy・不在（null）でfalse。存在チェックとして扱う
    load: ({ snapshot }) => !!snapshot.val(),
    skip: !currentUserId,
  });

  if (groupError || memberError) {
    return { enabled: false, loading: false };
  }

  // 未ログイン時はメンバー購読をスキップしているため、AIアシスタントは無効扱い
  if (!currentUserId) {
    return { enabled: false, loading: false };
  }

  return { enabled: !!groupEnabled && !!memberEnabled, loading: groupLoading || memberLoading };
}
